import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../ui/Modal';

/**
 * Apply flows with an independent "don't show again" preference. Dismissing
 * the warning on one page never suppresses it on the others - each flow
 * persists its choice under its own localStorage key.
 */
export type ContentMasterApplyWarningFlow = 'single' | 'bulk-review' | 'listing-review';

const dismissKeyFor = (flow: ContentMasterApplyWarningFlow): string =>
  `cm_apply_warning_dismissed:${flow}`;

/** Reads the persisted "don't show again" preference. Safe on SSR / blocked storage. */
export function isApplyWarningDismissed(flow: ContentMasterApplyWarningFlow): boolean {
  try {
    return localStorage.getItem(dismissKeyFor(flow)) === '1';
  } catch {
    return false;
  }
}

/** Persists the "don't show again" preference. */
export function setApplyWarningDismissed(flow: ContentMasterApplyWarningFlow): void {
  try {
    localStorage.setItem(dismissKeyFor(flow), '1');
  } catch {
    // Storage may be unavailable (private mode / quota). Non-fatal: the user
    // will simply see the warning again next time.
  }
}

export interface ContentMasterApplyWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Apply flow this instance belongs to - selects the dismiss storage key. */
  flow: ContentMasterApplyWarningFlow;
  /**
   * Called once the user consents. The component has already persisted the
   * "don't show again" choice (if checked) before invoking this.
   */
  onConfirm: () => void;
  isSubmitting?: boolean;
}

/**
 * Consent banner shown before content is pushed to the database (Wawi).
 *
 * Applying overwrites live product data and can break production listings,
 * so the user must explicitly confirm. A "don't show again" checkbox stores
 * the choice in localStorage per {@link ContentMasterApplyWarningFlow} - the
 * single-product, bulk-review and listing-review flows are independent.
 */
const ContentMasterApplyWarningModal: React.FC<ContentMasterApplyWarningModalProps> = ({
  isOpen,
  onClose,
  flow,
  onConfirm,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // Reset the checkbox every time the modal is (re)opened so a stale check
  // from a previous open never leaks into the next decision.
  useEffect(() => {
    if (isOpen) {
      setDontShowAgain(false);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (isSubmitting) {
      return;
    }
    if (dontShowAgain) {
      setApplyWarningDismissed(flow);
    }
    onConfirm();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={isSubmitting ? () => undefined : onClose}
      title=""
      hideCloseButton
      isCentered
      className="content-master-apply-warning-modal"
    >
      <div className="content-master-apply-warning-modal__inner">
        <div className="content-master-apply-warning-modal__icon" aria-hidden>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <h3 className="content-master-apply-warning-modal__title">
          {t('content_master_apply_warning_title')}
        </h3>

        <div className="content-master-apply-warning-modal__banner" role="alert">
          <p>{t('content_master_apply_warning_body')}</p>
        </div>

        <label className="content-master-apply-warning-modal__checkbox">
          <input
            type="checkbox"
            checked={dontShowAgain}
            disabled={isSubmitting}
            onChange={(event) => setDontShowAgain(event.target.checked)}
          />
          <span>{t('content_master_apply_warning_dont_show_again')}</span>
        </label>

        <div className="content-master-apply-warning-modal__buttons">
          <button
            type="button"
            className="content-master-apply-warning-modal__button content-master-apply-warning-modal__button--cancel"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t('cancelText')}
          </button>
          <button
            type="button"
            className="content-master-apply-warning-modal__button content-master-apply-warning-modal__button--confirm"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {t('content_master_apply_warning_confirm')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ContentMasterApplyWarningModal;
